import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@/hooks/apollo/useQuery';
import { CustomOption } from '@/components/CustomTravel/CustomInit';
import { CustomTravelType } from '@/types/apollo';

interface VisitArea {
  cityCode: string;
  cityName: string;
  countryCode: string;
  countryName: string;
}

const useCustomInit = ({ landingUrl, productId }: { landingUrl: string; productId: string }) => {
  const { data, loading } = useQuery(localQuery, { variables: { landingUrl, productId } });

  function moveToLast(customOptions: CustomOption[], groupType: string): CustomOption[] {
    const filteredArray = customOptions?.filter((item) => item.groupType !== groupType) || [];
    const targetItem = customOptions?.find((item) => item.groupType === groupType);
    if (targetItem) {
      filteredArray.push(targetItem);
    }
    return filteredArray;
  }

  function groupByCountry(input: VisitArea[]) {
    const maxCountry = 10;
    const maxCity = 3;
    return input.reduce((acc: any, cur: VisitArea) => {
      const { countryCode, countryName, cityCode, cityName } = cur;
      const country = acc.find((c: { countryCode: string }) => c.countryCode === cur.countryCode);
      if (acc.length < maxCountry) {
        if (!country) {
          return [...acc, { countryCode, countryName, cities: [{ cityCode, cityName }] }];
        }

        if (country.cities.length < maxCity) {
          country.cities.push({ cityName, cityCode });
        }
      }

      return acc;
    }, []);
  }

  const customInit = useMemo(() => {
    const { customOptions, overseasProduct, overseasAgent, customRequirements } = data || {};
    const agentAvailable = overseasAgent?.customActive && overseasAgent?.customGroupTypes?.length > 0;
    const productAvailable = !!overseasProduct;
    const availableCustomType = [CustomTravelType.Active, CustomTravelType.CustomOnly];
    const productFamilyAvailable = !!availableCustomType.includes(overseasProduct?.productFamily?.customTravelType);
    return {
      customAvailable: agentAvailable && productAvailable && productFamilyAvailable,
      customRequirements,
      customOptions: moveToLast(customOptions, 'etc'),
      overseasProduct: {
        ...overseasProduct,
        productFamily: {
          ...overseasProduct?.productFamily,
          visitAreas: groupByCountry(overseasProduct?.productFamily?.visitAreas || []),
        },
      },
      overseasAgent,
    };
  }, [data]);
  return {
    loading,
    ...customInit,
  };
};

const localQuery = gql`
  query getCustomInit($landingUrl: String!, $productId: String!) {
    customOptions(landingUrl: $landingUrl) {
      groupType
      groupName
      groupDescription
      specialNotes {
        name
        code
        isDirectInput
      }
      mealOrShoppings {
        name
        code
      }
      additionalRequirementCheckList {
        code
        name
      }
    }
    customRequirements {
      code
      name
    }
    overseasProduct(landingUrl: $landingUrl, productId: $productId) {
      _id
      agtCode
      productId
      productFamilyId
      productName
      beginDate
      endDate
      overseasBookMinSeat
      productFamily {
        _id
        images
        visitAreas {
          cityName
          cityCode
          countryName
          countryCode
        }
        trafficAir {
          detail {
            departures {
              airlineName
            }
          }
        }
        visitCountryCodes
        customTravelType
      }
    }
    overseasAgent(landingUrl: $landingUrl) {
      _id
      agtCode
      customActive
      customGroupTypes
      customKakaoActive
      agtLogoUrl
      name
    }
  }
`;

export default useCustomInit;
